import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, BellRing, Check, Sparkles, Loader2 } from "lucide-react";

const TYPES = ["birthday","anniversary","mahadasha","yearly_horoscope","gemstone_replacement","vastu_review","pending_payment","upcoming_appointment","custom"];
const CHANNELS = ["in_app","email","sms","whatsapp"];

export default function Followups() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(null);
  const [form, setForm] = useState({ client_id: "", client_name: "", title: "", type: "custom", due_date: new Date().toISOString().slice(0,10), channel: "in_app", message: "" });

  const load = async () => { const { data } = await api.get("/followups"); setItems(data); };
  useEffect(() => { load(); api.get("/clients").then(r => setClients(r.data)); }, []);

  const create = async () => {
    if (!form.title) return toast.error("Title required");
    await api.post("/followups", form);
    toast.success("Follow-up added");
    setOpen(false); load();
  };

  const done = async (id) => { await api.patch(`/followups/${id}`, { status: "done" }); load(); };

  const generate = async (f) => {
    setAiBusy(f.id);
    try {
      const { data } = await api.post("/ai/followup-message", { channel: f.channel, client_name: f.client_name || "there", reason: f.title });
      await api.patch(`/followups/${f.id}`, { message: data.message });
      toast.success("Message drafted");
      load();
    } catch { toast.error("AI unavailable"); }
    setAiBusy(null);
  };

  return (
    <div className="space-y-6" data-testid="followups-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400">Reminders</div>
          <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight mt-1">Follow-ups</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#1E3A8A] btn-pill h-11 px-5" data-testid="new-followup-btn"><Plus className="w-4 h-4 mr-1.5" /> New follow-up</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-2xl">Create follow-up</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Client (optional)</Label>
                <Select value={form.client_id} onValueChange={v => { const c = clients.find(x => x.id === v); setForm({...form, client_id:v, client_name: c?.full_name || ""}); }}>
                  <SelectTrigger><SelectValue placeholder="Choose client" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Field label="Title"><Input value={form.title} onChange={e=>setForm({...form,title:e.target.value})} /></Field></div>
              <Field label="Type">
                <Select value={form.type} onValueChange={v=>setForm({...form,type:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t.replace('_',' ')}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Due date"><Input type="date" value={form.due_date} onChange={e=>setForm({...form,due_date:e.target.value})} /></Field>
              <Field label="Channel">
                <Select value={form.channel} onValueChange={v=>setForm({...form,channel:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CHANNELS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button onClick={create} className="bg-[#1E3A8A] btn-pill">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {items.length === 0 && <Card className="p-12 rounded-2xl text-center border-dashed"><BellRing className="w-10 h-10 mx-auto text-slate-400" strokeWidth={1.25} /><p className="mt-3 text-slate-500">No pending follow-ups.</p></Card>}
        {items.map(f => (
          <Card key={f.id} className="p-5 rounded-2xl card-lift" data-testid={`followup-${f.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Badge className="bg-violet-100 text-violet-800">{f.type.replace('_',' ')}</Badge>
                  <Badge variant="outline">{f.channel}</Badge>
                  <span className="text-xs text-slate-500 font-mono">Due {(f.due_date || "").slice(0,10)}</span>
                </div>
                <div className="mt-2 font-medium">{f.title}</div>
                {f.client_name && <div className="text-sm text-slate-500">For: {f.client_name}</div>}
                {f.message && <div className="mt-2 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-sm">{f.message}</div>}
              </div>
              <div className="flex flex-col gap-2">
                <Button size="sm" variant="outline" onClick={() => generate(f)} disabled={aiBusy === f.id} data-testid={`ai-draft-${f.id}`}>
                  {aiBusy === f.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1 text-amber-500" />} AI Draft
                </Button>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={()=>done(f.id)} data-testid={`done-${f.id}`}><Check className="w-3 h-3 mr-1" /> Done</Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (<div><Label className="text-xs uppercase tracking-widest text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>);
